import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Route: /services/auto-repair
// Add to App.jsx or router file:
//   import AutoRepairPage from "./pages/AutoRepairPage";
//   <Route path="/services/auto-repair" element={<AutoRepairPage />} />
// Local dev: http://localhost:3000/services/auto-repair
//
// SEARCH INTEGRATION:
// The location search bar routes to: /search?category=auto-repair&location=ENCODED_LOCATION
// Your SearchResultsPage should read:
//   const params = new URLSearchParams(location.search);
//   const category = params.get("category");   // "auto-repair"
//   const loc      = params.get("location");   // e.g. "Westlands, Nairobi"
// Then filter your Supabase pros query by both fields.

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const CITIES = [
  "Nairobi", "Mombasa", "Kisumu", "Nakuru", "Eldoret",
  "Thika", "Meru", "Nyeri", "Machakos", "Malindi",
  "Westlands, Nairobi", "Kilimani, Nairobi", "Kasarani, Nairobi",
  "Langata, Nairobi", "Embakasi, Nairobi", "Karen, Nairobi",
  "Nyali, Mombasa", "Bamburi, Mombasa", "Likoni, Mombasa",
];

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:    <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:   <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    Search:       <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    MapPin:       <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    CheckCircle:  <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Star:         <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    ShieldCheck:  <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    BadgeCheck:   <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Wrench:       <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    Zap:          <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Clock:        <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Truck:        <><path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3"/><rect width="7" height="7" x="14" y="10" rx="1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></>,
    Phone:        <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    ChevronDown:  <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:    <><path d="m18 15-6-6-6 6"/></>,
    Gauge:        <><path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/></>,
    Droplets:     <><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"/></>,
    Battery:      <><rect width="16" height="10" x="2" y="7" rx="2"/><line x1="22" x2="22" y1="11" y2="13"/><line x1="6" x2="6" y1="11" y2="13"/><line x1="10" x2="10" y1="11" y2="13"/></>,
    Wind:         <><path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2"/><path d="M9.6 4.6A2 2 0 1 1 11 8H2"/><path d="M12.6 19.4A2 2 0 1 0 14 16H2"/></>,
    AlertTriangle:<><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z"/><path d="M12 9v4"/><path d="M12 17h.01"/></>,
    X:            <><path d="M18 6 6 18"/><path d="m6 6 12 12"/></>,
    Car:          <><path d="M19 17H5a2 2 0 0 1-2-2V9a2 2 0 0 1 .59-1.41l2-2A2 2 0 0 1 7 5h10a2 2 0 0 1 1.41.59l2 2A2 2 0 0 1 21 9v6a2 2 0 0 1-2 2z"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="16.5" cy="17.5" r="2.5"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Data ── */
const SERVICES = [
  {
    icon: "Wrench",
    label: "General repairs and servicing",
    desc: "Engine checks, oil changes, filter replacements, belt servicing, and scheduled maintenance for all vehicle types.",
    range: "KES 1,500 – 8,000",
    note: "per service",
    vehicles: ["Cars", "Motorbikes", "Tuk-tuks", "Vans"],
  },
  {
    icon: "Gauge",
    label: "Brake and suspension work",
    desc: "Brake pad replacement, disc resurfacing, shock absorber and suspension diagnostics, wheel alignment.",
    range: "KES 2,500 – 12,000",
    note: "per job",
    vehicles: ["Cars", "Vans", "Trucks"],
  },
  {
    icon: "Droplets",
    label: "Fluid top-ups and leak repairs",
    desc: "Engine oil, coolant, brake fluid, transmission fluid, power steering fluid. Leak tracing and sealing.",
    range: "KES 800 – 4,500",
    note: "per job",
    vehicles: ["Cars", "Motorbikes", "Tuk-tuks", "Vans"],
  },
  {
    icon: "Battery",
    label: "Battery and electrical diagnostics",
    desc: "Battery testing and replacement, alternator checks, starter motor diagnostics, fuse and wiring issues.",
    range: "KES 1,200 – 7,000",
    note: "per job",
    vehicles: ["Cars", "Motorbikes", "Vans", "Trucks"],
  },
  {
    icon: "Wind",
    label: "Tyre services",
    desc: "Tyre change and rotation, puncture repair, wheel balancing, spare fitting. Mobile service to your location.",
    range: "KES 500 – 3,500",
    note: "per tyre",
    vehicles: ["Cars", "Motorbikes", "Tuk-tuks", "Vans"],
  },
  {
    icon: "AlertTriangle",
    label: "Breakdown and emergency callouts",
    desc: "Flat batteries, stalled engines, roadside diagnostics, on-the-spot tyre changes. Available same-day.",
    range: "KES 2,000 – 10,000",
    note: "per callout",
    vehicles: ["Cars", "Motorbikes", "Tuk-tuks", "Vans", "Trucks"],
  },
];

const STEPS = [
  {
    num: "01", icon: "Search",
    title: "Search for a mechanic near you",
    desc: "Enter your location and the type of work you need. JiraniApp instantly surfaces verified mobile mechanics in your area with ratings, experience, and transparent price ranges. Filter by vehicle type, availability, or service category.",
  },
  {
    num: "02", icon: "BadgeCheck",
    title: "Choose a verified mechanic",
    desc: "Every mechanic on JiraniApp has been identity-verified and reviewed by real clients. Read their service history, check their ratings, and confirm they work on your vehicle type before booking. No surprises.",
  },
  {
    num: "03", icon: "MapPin",
    title: "Book at your location",
    desc: "Select a time, enter your exact location, whether home, office, roadside, or wherever your vehicle is, and add any notes about the problem. Your mechanic confirms the booking and comes to you. No towing required.",
  },
  {
    num: "04", icon: "CheckCircle",
    title: "Get it fixed and pay securely",
    desc: "The mechanic diagnoses and repairs your vehicle on-site. Any material or parts costs are communicated upfront before work begins. Pay through JiraniApp via M-Pesa once the job is complete.",
  },
];

const TESTIMONIALS = [
  {
    name: "Kevin M.",
    location: "Westlands, Nairobi",
    vehicle: "Toyota Fielder",
    rating: 5,
    text: "My car stalled on Waiyaki Way at 7 AM. I booked through JiraniApp and a mechanic arrived within 40 minutes. He diagnosed a faulty fuel pump relay on the spot and had me moving in under an hour. The whole thing cost KES 3,200 including the relay part. I did not have to tow anything.",
  },
  {
    name: "Wanjiru N.",
    location: "Kilimani, Nairobi",
    vehicle: "Honda Fit",
    rating: 5,
    text: "I had been putting off my service for months because I did not want to spend a whole Saturday at a garage. The mechanic from JiraniApp came to my building on a Tuesday evening, did a full service including oil change and filters in my parking lot, and was gone in two hours. Easier than anything I have tried before.",
  },
  {
    name: "Omar A.",
    location: "Nyali, Mombasa",
    vehicle: "Bajaj Boxer motorbike",
    rating: 5,
    text: "I use my motorbike for deliveries so downtime directly costs me money. The mechanic on JiraniApp came the same morning I booked, replaced my brake pads and adjusted the chain, and charged me KES 2,100. He also pointed out that my rear tyre was close to worn and showed me on the tyre itself. That kind of honesty keeps me coming back.",
  },
];

const FAQS = [
  {
    q: "Does the mechanic bring their own tools and parts?",
    a: "Mechanics on JiraniApp bring their own tools and diagnostic equipment to every callout. For parts, the approach varies: some mechanics carry common parts like oil filters, brake pads, and belts in their van and will quote you the part cost as part of the job. For less common parts, the mechanic will source them, confirm the cost with you before purchasing, and factor them into the final invoice. You will always know what parts cost before they are fitted.",
  },
  {
    q: "How accurate are the price ranges shown?",
    a: "The ranges shown on this page are representative of what verified mechanics on JiraniApp charge for standard jobs across Kenya. The actual cost for your specific vehicle depends on the make and model, the severity of the issue, and whether any parts need to be sourced. Mechanics provide a verbal or written estimate after the initial assessment, before starting any substantive work. You confirm before they proceed.",
  },
  {
    q: "What happens if the mechanic cannot fix the problem on-site?",
    a: "Some repairs genuinely require workshop equipment that cannot be brought to a location. If the mechanic determines on-site that the job requires more than a mobile visit can provide, they will tell you clearly, explain what is needed, and in most cases can recommend the next step. You will only pay the callout assessment fee, not the full repair cost, if no substantive work was completed. This is covered under the Jirani Guarantee.",
  },
  {
    q: "Can I book a mechanic for a pre-purchase inspection?",
    a: "Yes. Pre-purchase vehicle inspections are one of the most popular bookings in this category. You arrange for a verified mechanic to meet you at the location of the vehicle you are considering buying and carry out a thorough assessment: engine, undercarriage, bodywork, electrical, tyres, and fluid systems. They give you a clear report of what they found. It is one of the best investments available when buying a used vehicle.",
  },
  {
    q: "Do mechanics on JiraniApp work on all vehicle makes?",
    a: "Most mechanics on the platform work on Japanese and European makes that are common in Kenya: Toyota, Nissan, Subaru, Honda, Mitsubishi, Mazda, Volkswagen, and others. Profiles specify the makes and models a mechanic is experienced with. Some specialize in specific brands. When searching, you can filter by make to ensure the mechanic you book has relevant experience with your vehicle.",
  },
  {
    q: "What if the repair fails or the problem returns shortly after?",
    a: "If a repair does not hold or the same problem recurs within a reasonable period, you can raise a dispute through JiraniApp. Our Trust and Safety team reviews the case, looks at the job record, and works with both you and the mechanic to determine whether a revisit or partial refund is warranted. Most mechanics will return to address a recurring issue without needing a formal dispute, because their rating depends on it.",
  },
];

/* ── Location search bar ── */
function LocationSearchBar({ onSearch }) {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (value.length < 1) { setSuggestions([]); return; }
    const q = value.toLowerCase();
    setSuggestions(CITIES.filter(c => c.toLowerCase().includes(q)).slice(0, 6));
  }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setSuggestions([]); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const submit = (loc) => {
    setSuggestions([]);
    onSearch(loc || value);
  };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%", maxWidth: 520 }}>
      <div style={{ display: "flex", gap: 0, borderRadius: 12, overflow: "hidden", border: `2px solid ${focused ? T.g500 : T.n200}`, background: T.white, transition: "border-color .18s", boxShadow: focused ? `0 0 0 4px ${T.g100}` : "0 2px 12px rgba(0,0,0,.08)" }}>
        <div style={{ display: "flex", alignItems: "center", paddingLeft: 14, color: T.n400, flexShrink: 0 }}>
          <Icon name="MapPin" size={18} color={focused ? T.g500 : T.n400} strokeWidth={1.8} />
        </div>
        <input
          value={value}
          onChange={e => setValue(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={e => e.key === "Enter" && submit()}
          placeholder="Enter your city or estate..."
          style={{ flex: 1, padding: "15px 12px", border: "none", outline: "none", fontSize: 15, fontFamily: T.sans, color: T.n900, background: "transparent" }}
        />
        <button onClick={() => submit()} style={{ padding: "0 22px", background: T.g500, border: "none", cursor: "pointer", color: T.white, fontSize: 14, fontWeight: 600, fontFamily: T.sans, transition: "background .15s", display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}
          onMouseEnter={e => e.currentTarget.style.background = T.g600}
          onMouseLeave={e => e.currentTarget.style.background = T.g500}>
          Find mechanics
          <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
        </button>
      </div>
      {suggestions.length > 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: T.white, borderRadius: 12, border: `1px solid ${T.n150}`, boxShadow: "0 8px 32px rgba(0,0,0,.12)", zIndex: 100, overflow: "hidden" }}>
          {suggestions.map((s, i) => (
            <button key={i} onMouseDown={() => { setValue(s); submit(s); }}
              style={{ width: "100%", padding: "12px 16px", background: "none", border: "none", textAlign: "left", cursor: "pointer", fontSize: 14, fontFamily: T.sans, color: T.n700, display: "flex", alignItems: "center", gap: 10, borderBottom: i < suggestions.length - 1 ? `1px solid ${T.n100}` : "none" }}
              onMouseEnter={e => e.currentTarget.style.background = T.n50}
              onMouseLeave={e => e.currentTarget.style.background = "none"}>
              <Icon name="MapPin" size={14} color={T.n400} strokeWidth={1.8} />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Service card ── */
function ServiceCard({ service }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ padding: "22px", borderRadius: 16, border: `1.5px solid ${hovered ? T.g200 : T.n150}`, background: hovered ? T.g50 : T.white, transition: "all .18s", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: hovered ? T.g100 : T.n50, border: `1px solid ${hovered ? T.g200 : T.n150}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .18s" }}>
        <Icon name={service.icon} size={19} color={hovered ? T.g600 : T.n400} strokeWidth={1.6} />
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 6, letterSpacing: "-0.01em" }}>{service.label}</div>
        <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.7, margin: "0 0 12px" }}>{service.desc}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
          {service.vehicles.map((v, i) => (
            <span key={i} style={{ fontSize: 11, color: hovered ? T.g700 : T.n500, background: hovered ? T.white : T.n50, border: `1px solid ${hovered ? T.g100 : T.n150}`, borderRadius: 99, padding: "2px 9px", fontWeight: 500, fontFamily: T.sans, transition: "all .18s" }}>{v}</span>
          ))}
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${hovered ? T.g100 : T.n100}`, paddingTop: 10 }}>
        <div style={{ fontFamily: T.serif, fontSize: 19, fontWeight: 400, color: hovered ? T.g700 : T.n900, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{service.range}</div>
        <div style={{ fontSize: 11, color: T.n400, marginTop: 2 }}>{service.note}</div>
      </div>
    </div>
  );
}

/* ── Step ── */
function Step({ step, isLast }) {
  return (
    <div style={{ display: "flex", gap: 20, position: "relative" }}>
      {!isLast && (
        <div style={{ position: "absolute", left: 23, top: 52, bottom: -16, width: 1, background: `linear-gradient(to bottom, ${T.g200}, ${T.n100})` }} />
      )}
      <div style={{ width: 48, height: 48, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
        <Icon name={step.icon} size={20} color={T.g500} strokeWidth={1.6} />
      </div>
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 40 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.1em", marginBottom: 5 }}>STEP {step.num}</div>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(18px,2.8vw,22px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.02em", lineHeight: 1.2 }}>{step.title}</h3>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.78, margin: 0 }}>{step.desc}</p>
      </div>
    </div>
  );
}

/* ── Testimonial ── */
function Testimonial({ t }) {
  const initials = t.name.split(" ").map(w => w[0]).slice(0, 2).join("");
  const palette = [
    { bg: T.g50, text: T.g700, border: T.g100 },
    { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
    { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  ];
  const c = palette[t.name.charCodeAt(0) % palette.length];
  return (
    <div style={{ padding: "24px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 2 }}>
        {Array.from({ length: t.rating }).map((_, i) => (
          <Icon key={i} name="Star" size={14} color="#f59e0b" strokeWidth={0} />
        ))}
      </div>
      <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.8, margin: 0, fontStyle: "italic" }}>"{t.text}"</p>
      <div style={{ borderTop: `1px solid ${T.n100}`, paddingTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: c.bg, border: `1.5px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: c.text, fontFamily: T.sans, flexShrink: 0 }}>{initials}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n900 }}>{t.name}</div>
            <div style={{ fontSize: 12, color: T.n400 }}>{t.location}</div>
          </div>
        </div>
        <span style={{ fontSize: 11, color: T.n400, background: T.n50, border: `1px solid ${T.n150}`, borderRadius: 99, padding: "3px 10px", fontWeight: 500 }}>{t.vehicle}</span>
      </div>
    </div>
  );
}

/* ── FAQ ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: "20px 0", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 20, fontFamily: T.sans }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: T.n800, lineHeight: 1.5, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, border: `1px solid ${open ? T.g500 : T.n200}`, background: open ? T.g500 : "transparent", color: open ? T.white : T.n400, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s", transform: open ? "rotate(45deg)" : "none", fontSize: 17, fontWeight: 300, lineHeight: 1 }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 500 : 0, transition: "max-height .3s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ margin: "0 0 20px", fontSize: 14, color: T.n600, lineHeight: 1.8, paddingRight: 40, fontFamily: T.sans }}>{a}</p>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function AutoRepairPage() {
  useFonts();
  const navigate = useNavigate();

  const handleSearch = (location) => {
    if (!location.trim()) return;
    navigate(`/search?category=auto-repair&location=${encodeURIComponent(location.trim())}`);
  };

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`* { box-sizing: border-box; } ::-webkit-scrollbar { display: none; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <button onClick={() => navigate("/pro-onboarding?category=auto-repair")}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${T.n200}`, cursor: "pointer", color: T.n600, fontSize: 13, fontFamily: T.sans, padding: "8px 14px", borderRadius: 8, fontWeight: 500 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.g500; e.currentTarget.style.color = T.g600; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
          <Icon name="Wrench" size={13} strokeWidth={2} />
          Are you a mechanic? Join JiraniApp
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(52px,7vw,88px) 20px clamp(44px,5vw,68px)", borderBottom: `1px solid ${T.n100}`, background: `linear-gradient(to bottom, ${T.g50}, ${T.white})` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 20 }}>
            <Icon name="Car" size={14} color={T.g500} strokeWidth={2.2} />
            Auto and vehicle repair
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,64px)", fontWeight: 400, margin: "0 0 16px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em", maxWidth: 620 }}>
            Verified mechanics who come<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>to your vehicle</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 32px", maxWidth: 500 }}>
            Book a vetted mobile mechanic in your city. Cars, motorbikes, tuk-tuks, and vans. No garage trip, no towing. They diagnose and fix on-site at your home, office, or roadside.
          </p>
          <LocationSearchBar onSearch={handleSearch} />
          <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 16 }}>
            {[
              { icon: "BadgeCheck", text: "Identity-verified mechanics" },
              { icon: "Zap",        text: "Same-day bookings available" },
              { icon: "ShieldCheck",text: "Jirani Guarantee on every job" },
            ].map((b, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: T.n600 }}>
                <Icon name={b.icon} size={15} color={T.g500} strokeWidth={1.8} />
                {b.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
          {[
            { icon: "BadgeCheck",  label: "Every mechanic ID-verified before going live" },
            { icon: "Star",        label: "Real reviews from verified completed bookings" },
            { icon: "Truck",       label: "Mobile service to any location in your city" },
            { icon: "ShieldCheck", label: "Jirani Guarantee covers every booking" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "20px 14px", display: "flex", alignItems: "center", gap: 10, borderRight: i < 3 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={16} color={T.g500} strokeWidth={1.6} />
              <span style={{ fontSize: 12, color: T.n600, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px" }}>

        {/* ══ SERVICES ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,72px)", marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Services covered</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What our mechanics <em style={{ fontStyle: "italic", color: T.g600 }}>handle on-site</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            From routine servicing to emergency callouts. All work is done at your location with transparent pricing agreed before any work begins.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 14 }}>
            {SERVICES.map((s, i) => <ServiceCard key={i} service={s} />)}
          </div>
          <div style={{ marginTop: 18, padding: "14px 18px", borderRadius: 12, background: T.amberBg, border: `1px solid ${T.amberBorder}` }}>
            <p style={{ fontSize: 13, color: "#92400e", margin: 0, lineHeight: 1.7 }}>
              Price ranges are indicative. Actual cost depends on vehicle make, model, and job complexity. Your mechanic provides a firm quote after the initial assessment, before starting work.
            </p>
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>How it works</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Booked, arrived, fixed. <em style={{ fontStyle: "italic", color: T.g600 }}>That simple.</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 32px", lineHeight: 1.65 }}>
            No garage waiting rooms. No towing costs. No half-day lost. Four steps from booking to back on the road.
          </p>
          <div style={{ paddingLeft: 4 }}>
            {STEPS.map((step, i) => <Step key={i} step={step} isLast={i === STEPS.length - 1} />)}
          </div>
        </div>

        {/* ══ TRUST BLOCK ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)", padding: "clamp(28px,5vw,44px)", borderRadius: 20, background: T.g50, border: `1px solid ${T.g100}` }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 11, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 14 }}>
            <Icon name="ShieldCheck" size={13} color={T.g500} strokeWidth={2} />
            The Jirani Guarantee
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,30px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Every booking is <em style={{ fontStyle: "italic", color: T.g600 }}>protected</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 24px" }}>
            Letting someone work on your vehicle is a matter of trust. We verify every mechanic's identity before they are listed, require them to maintain strong review scores to stay active, and back every booking with our dispute resolution process.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {[
              { icon: "BadgeCheck",  title: "ID-verified mechanics",       desc: "Every mechanic submits identity documents before their profile goes live. Unverified Pros cannot take bookings." },
              { icon: "Star",        title: "Genuine review scores",        desc: "Reviews are tied to completed bookings only. No anonymous ratings, no fake reviews. What you read is what clients experienced." },
              { icon: "Phone",       title: "In-app communication",        desc: "All messages between you and your mechanic stay in JiraniApp, creating a clear record if anything needs to be reviewed." },
              { icon: "CheckCircle", title: "Dispute resolution included", desc: "If the repair fails or something goes wrong, raise a dispute within 48 hours. Our team reviews it and works toward a fair resolution." },
            ].map((item, i) => (
              <div key={i} style={{ padding: "18px", borderRadius: 14, background: T.white, border: `1px solid ${T.g100}`, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={item.icon} size={16} color={T.g500} strokeWidth={1.7} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.n900 }}>{item.title}</div>
                <p style={{ fontSize: 12, color: T.n600, lineHeight: 1.65, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ TESTIMONIALS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>From clients</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What drivers <em style={{ fontStyle: "italic", color: T.g600 }}>say</em>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 14 }}>
            {TESTIMONIALS.map((t, i) => <Testimonial key={i} t={t} />)}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Common questions</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Before you <em style={{ fontStyle: "italic", color: T.g600 }}>book</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            The questions clients ask most when booking a mechanic for the first time.
          </p>
          <div>
            {FAQS.map((item, i) => <FaqItem key={i} {...item} />)}
          </div>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(36px,5vw,56px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50, marginBottom: 32 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon name="Wrench" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Your vehicle sorted,<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>without leaving home</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 auto 28px", lineHeight: 1.75, maxWidth: 400 }}>
            Find a verified mobile mechanic in your area and book in under two minutes.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/search?category=auto-repair")} style={{ padding: "14px 30px", background: T.g500, color: T.white, border: "none", borderRadius: 11, fontSize: 15, fontWeight: 600, fontFamily: T.sans, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Find a mechanic near me
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={() => navigate("/how-it-works")} style={{ padding: "14px 24px", background: "transparent", color: T.n700, border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 15, fontWeight: 600, fontFamily: T.sans, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              How JiraniApp works
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}